import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { useHRAuth } from '../../contexts/HRAuthContext';
import axios from 'axios';
import {
    Building2, Users, TrendingUp, BookOpen, Heart, Smile,
    ChevronRight, LogOut, RefreshCw, BarChart3, Activity
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HRDashboard = () => {
    const navigate = useNavigate();
    const { manager, logout } = useHRAuth();
    const [overview, setOverview] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchOverview();
    }, []);

    const fetchOverview = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await axios.get(`${API}/hr/reports/overview`, {
                withCredentials: true
            });
            setOverview(response.data);
        } catch (err) {
            setError('Erro ao carregar dados. Tente novamente.');
            console.error('Error fetching overview:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleLogout = async () => {
        await logout();
        navigate('/hr/login');
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Carregando relatórios...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
            {/* Header */}
            <div className="bg-white shadow-sm border-b">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex justify-between items-center">
                        <div className="flex items-center space-x-4">
                            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                <Building2 className="w-6 h-6 text-blue-600" />
                            </div>
                            <div>
                                <h1 className="text-xl font-bold text-gray-900">
                                    {manager?.company_name || 'Portal RH'}
                                </h1>
                                <p className="text-sm text-gray-500">
                                    {manager?.name} - {manager?.email}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-3">
                            <Button
                                onClick={fetchOverview}
                                variant="outline"
                                size="sm"
                                className="flex items-center"
                            >
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Atualizar
                            </Button>
                            <Button
                                onClick={handleLogout}
                                variant="outline"
                                size="sm"
                                className="flex items-center text-red-600 hover:text-red-700"
                            >
                                <LogOut className="w-4 h-4 mr-2" />
                                Sair
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                        <p className="text-red-700">{error}</p>
                    </div>
                )}

                {/* Company Overview */}
                <div className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">
                        Visao Geral dos Grupos
                    </h2>
                    <p className="text-gray-600 mb-6">
                        Acompanhe o progresso e engajamento dos colaboradores de forma anonimizada
                    </p>
                </div>

                {/* Groups Grid */}
                {overview?.groups && overview.groups.length > 0 ? (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                        {overview.groups.map((group) => (
                            <Card
                                key={group.group}
                                className="p-6 hover:shadow-lg transition-shadow cursor-pointer"
                                onClick={() => navigate(`/hr/group/${encodeURIComponent(group.group)}`)}
                            >
                                <div className="flex justify-between items-start mb-4">
                                    <div>
                                        <h3 className="font-bold text-lg text-gray-900">
                                            {group.group}
                                        </h3>
                                        <p className="text-sm text-gray-500">
                                            {group.total_users} colaboradores
                                        </p>
                                    </div>
                                    <ChevronRight className="w-5 h-5 text-gray-400" />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="bg-blue-50 p-3 rounded-lg">
                                        <div className="flex items-center space-x-2 mb-1">
                                            <Activity className="w-4 h-4 text-blue-600" />
                                            <span className="text-xs text-gray-600">Ativos (7d)</span>
                                        </div>
                                        <p className="text-xl font-bold text-blue-600">
                                            {group.active_users_7d}
                                        </p>
                                    </div>
                                    <div className="bg-green-50 p-3 rounded-lg">
                                        <div className="flex items-center space-x-2 mb-1">
                                            <TrendingUp className="w-4 h-4 text-green-600" />
                                            <span className="text-xs text-gray-600">Nivel Medio</span>
                                        </div>
                                        <p className="text-xl font-bold text-green-600">
                                            {group.avg_level}
                                        </p>
                                    </div>
                                    <div className="bg-purple-50 p-3 rounded-lg">
                                        <div className="flex items-center space-x-2 mb-1">
                                            <BookOpen className="w-4 h-4 text-purple-600" />
                                            <span className="text-xs text-gray-600">Licoes</span>
                                        </div>
                                        <p className="text-xl font-bold text-purple-600">
                                            {group.total_lessons_completed}
                                        </p>
                                    </div>
                                    <div className="bg-orange-50 p-3 rounded-lg">
                                        <div className="flex items-center space-x-2 mb-1">
                                            <BarChart3 className="w-4 h-4 text-orange-600" />
                                            <span className="text-xs text-gray-600">XP Medio</span>
                                        </div>
                                        <p className="text-xl font-bold text-orange-600">
                                            {group.avg_xp}
                                        </p>
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>
                ) : (
                    <Card className="p-12 text-center">
                        <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-xl font-bold text-gray-900 mb-2">
                            Nenhum grupo encontrado
                        </h3>
                        <p className="text-gray-600">
                            Os grupos de colaboradores aparecerao aqui quando forem cadastrados.
                        </p>
                    </Card>
                )}

                {/* Quick Links */}
                <div className="grid md:grid-cols-3 gap-6">
                    <Card className="p-6 bg-gradient-to-br from-purple-50 to-white border-purple-200">
                        <div className="flex items-center space-x-3 mb-3">
                            <Heart className="w-6 h-6 text-purple-600" />
                            <h3 className="font-bold text-gray-900">Diario de Gatilhos</h3>
                        </div>
                        <p className="text-sm text-gray-600 mb-4">
                            Analise anonimizada das emocoes e gatilhos registrados pelos colaboradores.
                        </p>
                        <p className="text-xs text-gray-500">
                            Selecione um grupo para ver os detalhes
                        </p>
                    </Card>

                    <Card className="p-6 bg-gradient-to-br from-green-50 to-white border-green-200">
                        <div className="flex items-center space-x-3 mb-3">
                            <Smile className="w-6 h-6 text-green-600" />
                            <h3 className="font-bold text-gray-900">Check-in Diario</h3>
                        </div>
                        <p className="text-sm text-gray-600 mb-4">
                            Acompanhe o humor e energia media da equipe ao longo do tempo.
                        </p>
                        <p className="text-xs text-gray-500">
                            Selecione um grupo para ver os detalhes
                        </p>
                    </Card>

                    <Card className="p-6 bg-gradient-to-br from-blue-50 to-white border-blue-200">
                        <div className="flex items-center space-x-3 mb-3">
                            <BookOpen className="w-6 h-6 text-blue-600" />
                            <h3 className="font-bold text-gray-900">Progresso de Aprendizado</h3>
                        </div>
                        <p className="text-sm text-gray-600 mb-4">
                            Veja a evolucao da equipe nas licoes de Inteligência Relacional e inteligencia emocional.
                        </p>
                        <p className="text-xs text-gray-500">
                            Selecione um grupo para ver os detalhes
                        </p>
                    </Card>
                </div>

                {/* Footer info */}
                <div className="mt-8 text-center text-sm text-gray-500">
                    <p>
                        Todos os dados sao anonimizados para proteger a privacidade dos colaboradores.
                    </p>
                    <p className="mt-1">
                        Ultima atualizacao: {overview?.generated_at ? new Date(overview.generated_at).toLocaleString('pt-BR') : '-'}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default HRDashboard;

